<template>
  <div class="grid p-fluid mt-1">
    <div class="col-6 field">
      <TextInput :label="$t('Label')" v-model="properties.label" />
    </div>
    <div class="col-12 field">
      <EditorInput
        :label="$t('Description')"
        v-model="properties.description"
      />
    </div>
    <div class="col-12 field">
      <MultiUpload
        :label="$t('Icon')"
        v-model="properties.icon"
        :is-multiple="false"
      />
    </div>
    <div class="col-6 field">
      <ListInput
        :label="$t('Severity')"
        v-model="properties.severity"
        :options="['primary', 'secondary', 'info', 'warning', 'danger']"
      />
    </div>
    <div class="col-6 field">
      <ListInput
        :label="$t('Size')"
        v-model="properties.size"
        :options="['small', 'normal', 'large']"
      />
    </div>
    <div class="col-6 field">
      <CodeInput
      :label="$t('Button CSS')"
      v-model="properties.buttonStyle"
      height="80px"
      language="css"
      :disabled="disabled"
    />

    </div>
    <div class="col-6 field">
      <CodeInput
      :label="$t('Icon CSS')"
      v-model="properties.iconStyle"
      height="80px"
      language="css"
      :disabled="disabled"
    />

    </div>
    <div class="col-12 field">
      <div class="flex justify-content-between align-items-center p-inputtext">
        <div>{{ $t('Show label') }}</div>
        <InputSwitch v-model="properties.showLabel" />
      </div>
    </div>
    <div class="col-12 field">
      <div class="flex justify-content-between align-items-center p-inputtext">
        <div>{{ $t('Active') }}</div>
        <InputSwitch v-model="properties.active" />
      </div>
    </div>
    <div class="col-12 field">
      <div class="flex justify-content-between align-items-center p-inputtext">
        <div>{{ $t('Disabled') }}</div>
        <InputSwitch v-model="properties.disabled" />
      </div>
    </div>

    <div class="col-12 field">
      <TreeInput
        :label="$t('Action type')"
        v-model="action.actionType"
        :options="actionTypes"
        :error="errors.actionType"
        model-type="id"
      />
    </div>

    <template
      v-if="
        action.actionType && ['open_form_in_modal', 'open_form_in_page', 'map_interaction'].includes(action.actionType)
      "
    >
      <div class="col-12 field">
        <TreeInput
          :label="$t('Will trigger mode')"
          v-model="action.mapMode"
          label-key="title"
          item-key="identifier"
          model-type="id"
          :options="modes"
          :error="errors.action"
        />
      </div>
      <div v-if="action.mapMode" class="col-12 field">
        <TreeInput
          :label="$t('Will trigger action')"
          v-model="action.mapInteraction"
          :options="interactions"
          :error="errors.action"
          label-key="title"
          item-key="title"
          model-type="id"
        />
      </div>
    </template>

    <template v-if="action.actionType && ['map_interaction'].includes(action.actionType)">
      <div class="col-12 field">
        <TreeInput
          :label="$t('Will apply on layer')"
          v-model="actionLayer.documentId"
          :options="documents"
          :transform-to-tree="false"
          label-key="title"
          item-key="documentId"
          model-type="id"
          :error="errors.documentId"
        />
      </div>

      <div class="col-12 field">
        <MultiListInput
          :label="$t('Visible layers')"
          v-model="actionSettings.visibleLayers"
          :options="documents"
          optionLabel="title"
          optionValue="documentId"
          id-key="documentId"
          :disable-branch-nodes="true"
        />
      </div>
      <div class="col-12 flex justify-content-between">
        <div class="col-6 field m-3">
          <div class="flex align-items-center justify-content-between p-inputtext">
            <div>{{ $t('Info button active') }}</div>
            <InputSwitch v-model="actionSettings.infoButtonActive" />
          </div>
        </div>
        <div class="col-6 field">
          <NumberInput :label="$t('Animation duration (ms)')" v-model="actionSettings.animationDuration" />
        </div>
      </div>
      <div class="col-6 field">
      <TextInput :label="$t('Search error')" v-model="actionSettings.searchErrorMessage" />
    </div>
    </template>

    <template v-if="action.actionType && ['open_form_in_modal', 'open_form_in_page'].includes(action.actionType)">
      <div class="col-12 md:col-6 field p-fluid">
        <TreeInput
          :label="$t('Form category')"
          v-model="actionDocument.documentCategory"
          :options="documentCategories"
          label-key="title"
          item-key="documentCategoryId"
          model-type="object"
        />
      </div>
      <div class="col-12 md:col-6 field p-fluid">
        <TreeInput
          :label="$t('Form type')"
          v-model="actionDocument.documentType"
          :options="documentTypes"
          label-key="title"
          item-key="documentTypeId"
          model-type="object"
        />
      </div>
    </template>
    <template v-if="action.actionType && action.actionType === 'post_message'">
      <div class="col-12 field">
        <TextInput :label="$t('Message')" v-model="action.message" :error="errors.message" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import TextInput from '../../../inputs/TextInput.vue';
import { getNestedList } from '../../../../js/utils';
import { MapSettingsRegistry } from '../../../../js/Map/registry';
import { mapGetters } from 'vuex';
import TreeInput from '../../../inputs/TreeInput.vue';
import { v4 as uuid } from 'uuid';
import MultiListInput from '../../../inputs/MultiListInput.vue';
import NumberInput from '../../../inputs/NumberInput.vue';
import MultiUpload from "@/components/inputs/MultiUpload.vue";
import EditorInput from "@/components/inputs/EditorInput.vue";
import ListInput from "@/components/inputs/ListInput.vue";
import CodeInput from '../../../inputs/CodeInput.vue';

export default defineComponent({
  name: 'MapButtonControlConfiguration',
  components: {ListInput, EditorInput, MultiUpload, TextInput, TreeInput, MultiListInput, NumberInput, CodeInput },
  props: {
    customControl: {
      type: Object,
    },
    documents: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters({
      common: 'GET_COMMON_DATA',
    }),
    interactions() {
      if (this.action.mapMode) {
        return Object.keys(MapSettingsRegistry.getMode(this.action.mapMode).interactions);
      }
      return [];
    },
    modes() {
      return MapSettingsRegistry.getModes().map((el) => {
        return {
          title: el.title,
          identifier: el.identifier,
        };
      });
    },
    actionDocument: {
      get() {
        if (!this.action) this.action = {};
        if (!this.action.document)
          this.action.document = {
            documentCategory: null,
            documentType: null,
          };
        return this.action.document;
      },
      set(newValue) {
        if (!this.action) this.action = {};
        if (!this.action.document)
          this.action.document = {
            documentCategory: null,
            documentType: null,
          };
        this.action.document = newValue;
      },
    },
    actionLayer: {
      get() {
        if (!this.action) this.action = {};
        if (!this.action.layer) this.action.layer = {};
        return this.action.layer;
      },
      set(newValue) {
        if (!this.action) this.action = {};
        if (!this.action.layer) this.action.layer = {};
        this.action.layer = newValue;
      },
    },
    actionSettings: {
      get() {
        if (!this.action) this.action = {};
        if (!this.action.settings) this.action.settings = {};
        return this.action.settings;
      },
      set(newValue) {
        if (!this.action) this.action = {};
        if (!this.action.settings) this.action.settings = {};
        this.action.settings = newValue;
      },
    },
    documentTypes() {
      return getNestedList(
        this.common.documentTypes
          .filter((t) =>
            this.documentCategoryId ? t.documentCategoryId === this.documentCategoryId : t.documentTypeId >= 0
          )
          .map((x) => ({
            documentTypeId: x.documentTypeId,
            parentDocumentTypeId: x.parentDocumentTypeId,
            documentCategoryId: x.documentCategoryId,
            depth: x.depth,
            title: x.title,
          })),
        'documentTypeId',
        'parentDocumentTypeId',
        undefined,
        0
      );
    },
    properties() {
      if (!this.customControl.properties) this.customControl.properties = {};
      return this.customControl.properties;
    },
    action() {
      if (!this.properties.action)
        this.properties.action = {
          actionType: null,
          mapMode: null,
          mapInteraction: null,
          layer: { documentId: null },
          document: { documentCategory: null, documentType: null },
          message: '',
        };
      return this.properties.action;
    },
  },
  data() {
    return {
      errors: {},
      documentCategories: [],
      actionTypes: ['map_interaction', 'post_message', 'open_form_in_modal', 'open_form_in_page'],
    };
  },
  created() {
    this.documentCategories = getNestedList(
      this.common.documentCategories.map((x) => ({
        documentCategoryId: x.documentCategoryId,
        parentDocumentCategoryId: x.parentDocumentCategoryId,
        depth: x.depth,
        title: x.title,
      })),
      'documentCategoryId',
      'parentDocumentCategoryId',
      undefined,
      0
    );
  },
  mounted() {
    if (this.data) {
      this.customControl = { ...this.data };
      if (!this.action)
        this.action = {
          actionType: null,
          mapMode: null,
          mapInteraction: null,
          layer: { documentId: null },
          document: { documentCategory: null, documentType: null },
          message: '',
        };
      if (!this.properties) this.properties = {};
    }
  },
});
</script>

<style scoped lang="scss"></style>
